import { getFromState, useVariant } from '~/composables';
import { mapBlog } from '~/units/blogs/store';
import { ComponentMixin } from '~/mixins/component.mixin';
import type { BlogItem, ManualBlogs, ManualBlogsRepo } from '~/units/blogs/types';
import { LoadingStates, RepoStates } from '~/units/core/types';

export const ManualBlogsMixin = defineNuxtComponent({
  mixins: [ComponentMixin],

  computed: {
    isManualBlogsLoading() {
      return <boolean>getFromState(LoadingStates.IS_MANUAL_BLOGS_LOADING);
    },

    manualBlogsRepoRef(): ManualBlogs | undefined | null {
      const variant = useVariant(this.component);
      const data: ManualBlogsRepo = <ManualBlogsRepo>getFromState(RepoStates.MANUAL_BLOGS_REPO);

      // Get blog slugs from component variables
      const blogSlugs = this.getBlogSlugsFromVariables();

      if (!blogSlugs || blogSlugs.length === 0) {
        return null;
      }

      const identifier = `${variant}-${[...blogSlugs].sort().join('-')}`;

      return data && data.length > 0 ? data.find((item) => item.identifier === identifier) : null;
    },

    manualBlogs() {
      const manualBlogsData: ManualBlogs = <ManualBlogs>this.manualBlogsRepoRef;

      if (manualBlogsData && manualBlogsData.data) {
        return manualBlogsData.data.map((item: BlogItem) => {
          return mapBlog(item);
        });
      }
      return [];
    },

    manualBlogsTotal(): number {
      if (this.manualBlogsRepoRef && this.manualBlogsRepoRef.total) {
        return this.manualBlogsRepoRef.total;
      }
      return 0;
    },
  },

  methods: {
    /**
     * Extract blog slugs from component's grouped variables
     * Pattern: blogs_1_slug-text, blogs_2_slug-text, etc.
     */
    getBlogSlugsFromVariables(): string[] {
      const variables = this.component.variables;
      if (!variables) {
        return [];
      }

      // Return slugs ordered by their numeric index (blogs_1, blogs_2, ...)
      return Object.keys(variables)
        .map((key) => {
          const match = key.match(/blogs_(\d+)_slug-text/);
          if (!match) return null;
          const slug = variables[key] as string;
          if (!slug || !slug.trim()) return null;
          return { index: Number(match[1]), slug: slug.trim() };
        })
        .filter((x): x is { index: number; slug: string } => !!x)
        .sort((a, b) => a.index - b.index)
        .map((x) => x.slug);
    },
  },
});

